import { useEffect } from 'react';

import type { MapProps } from '../../types';
import { getPinsBounds } from '../../helpers';
import { log, withSinglePinBoundsOffsetsCoordinates } from '../../utils';
import { useNotMinimizedPinsRef } from '../useNotMinimizedPinsRef';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Adds provided offset to map, to prevent cases, when markers are hidden underneath external elements.
 * Uses `fitBounds` method from Google Maps JS API with the corresponding `padding`/`offset`.
 *
 * @see {@link https://developers.google.com/maps/documentation/javascript/reference/map#Map.panBy `panBy` docs}
 */
export const useMapOffset = (props: UseMapOffsetProps) => {
  const { isEnabled, googleMapRef, offsetY, pins } = props;

  const pinsRef = useNotMinimizedPinsRef(pins);

  // pan map based on offset values in `px`
  useEffect(() => {
    if (!isEnabled || !offsetY) return;

    if (!googleMapRef) {
      log.error('`map` object was not found!');

      return;
    }

    // ─────────────────────────────────────────────────────────────────────

    const bounds = getPinsBounds(
      withSinglePinBoundsOffsetsCoordinates(pinsRef.current),
    );

    if (!bounds || bounds.isEmpty()) return;

    googleMapRef?.fitBounds?.(bounds, { bottom: offsetY });

    return () => {
      googleMapRef?.fitBounds?.(bounds, 0);
    };
  }, [googleMapRef, isEnabled, offsetY, pinsRef]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseMapOffsetProps = Readonly<{
  isEnabled: boolean;
  googleMapRef?: google.maps.Map;
}> &
  Pick<MapProps, 'offsetY' | 'pins'>;
